import { useEffect, useRef, useState } from "react";

/**
 * <CropFocusInspector />
 *
 * Dev-only overlay that visualizes object-position cropping for any
 * <img>, <video>, or <picture> element opted-in via `data-focus="x%_y%"`.
 *
 * For each opted-in element it draws:
 *   • a crosshair on the configured focal point (mapped from source → rendered space)
 *   • a dashed rectangle showing the actual visible crop-region of the source
 *   • a label with the live container aspect-ratio + chosen object-position
 *
 * Renders nothing in production builds. Activate with ?focus=1 in the URL,
 * or by setting localStorage.cropFocusInspector = "1". Press the toggle
 * button (bottom-right) to hide/show without losing state.
 *
 * Usage on any image:
 *   <img
 *     src={...}
 *     data-focus="50%_38%"   // matches your active object-position breakpoint
 *     className="object-cover object-[50%_38%] ..."
 *   />
 *
 * The inspector reads the *rendered* CSS object-position at runtime, so the
 * data-focus attribute is optional — it only adds a label hint.
 */

type Marker = {
  el: HTMLElement;
  rect: DOMRect;
  natW: number;
  natH: number;
  objectPosition: string;
  focalHint?: string;
};

const isProd = import.meta.env.PROD;

function readEnabled(): boolean {
  if (typeof window === "undefined") return false;
  if (new URLSearchParams(window.location.search).has("focus")) return true;
  try {
    return window.localStorage.getItem("cropFocusInspector") === "1";
  } catch {
    return false;
  }
}

function parsePercent(v: string, fallback: number): number {
  const m = /(-?\d+(?:\.\d+)?)\s*%/.exec(v);
  return m ? parseFloat(m[1]) : fallback;
}

function getNaturalSize(el: HTMLElement): { w: number; h: number } | null {
  if (el instanceof HTMLImageElement && el.naturalWidth > 0) {
    return { w: el.naturalWidth, h: el.naturalHeight };
  }
  if (el instanceof HTMLVideoElement && el.videoWidth > 0) {
    return { w: el.videoWidth, h: el.videoHeight };
  }
  return null;
}

/**
 * Compute the rectangle (in container-pixel coords) that the *source* would
 * occupy, accounting for object-fit:cover + object-position. We scale the
 * source to the smaller cover-scale, then translate by object-position offsets.
 */
function computeCoverRect(
  containerW: number,
  containerH: number,
  natW: number,
  natH: number,
  posX: number, // 0..100
  posY: number // 0..100
) {
  const scale = Math.max(containerW / natW, containerH / natH);
  const scaledW = natW * scale;
  const scaledH = natH * scale;
  const overflowX = scaledW - containerW;
  const overflowY = scaledH - containerH;
  const offsetX = -overflowX * (posX / 100);
  const offsetY = -overflowY * (posY / 100);
  return { left: offsetX, top: offsetY, width: scaledW, height: scaledH };
}

const CropFocusInspector = () => {
  const [enabled, setEnabled] = useState<boolean>(() => !isProd && readEnabled());
  const [collapsed, setCollapsed] = useState(false);
  const [markers, setMarkers] = useState<Marker[]>([]);
  const rafRef = useRef<number | null>(null);

  // Listen for ?focus or storage toggles after mount.
  useEffect(() => {
    if (isProd) return;
    const onStorage = () => setEnabled(readEnabled());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Scan + recompute markers whenever the layout might have changed.
  useEffect(() => {
    if (!enabled || isProd) {
      setMarkers([]);
      return;
    }

    const scan = () => {
      const els = Array.from(
        document.querySelectorAll<HTMLElement>(
          'img[data-focus], video[data-focus], img[class*="object-["], video[class*="object-["]'
        )
      );
      const next: Marker[] = [];
      for (const el of els) {
        const size = getNaturalSize(el);
        if (!size) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width < 4 || rect.height < 4) continue;
        const cs = window.getComputedStyle(el);
        next.push({
          el,
          rect,
          natW: size.w,
          natH: size.h,
          objectPosition: cs.objectPosition || "50% 50%",
          focalHint: el.dataset.focus,
        });
      }
      setMarkers(next);
    };

    const schedule = () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(scan);
    };

    scan();
    const ro = new ResizeObserver(schedule);
    ro.observe(document.documentElement);

    const mo = new MutationObserver(schedule);
    mo.observe(document.body, {
      attributes: true,
      attributeFilter: ["class", "style", "src", "data-focus"],
      subtree: true,
      childList: true,
    });

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);

    // Re-scan when images/videos finish loading and gain natural dimensions.
    const loadHandler = () => schedule();
    document.addEventListener("load", loadHandler, true);
    document.addEventListener("loadedmetadata", loadHandler, true);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("load", loadHandler, true);
      document.removeEventListener("loadedmetadata", loadHandler, true);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);

  if (isProd || !enabled) return null;

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483646,
        pointerEvents: "none",
      }}
    >
      {!collapsed &&
        markers.map((m, i) => {
          const [posX, posY] = m.objectPosition.split(/\s+/);
          const px = parsePercent(posX, 50);
          const py = parsePercent(posY, 50);

          // Crosshair = focal point inside the *rendered* container.
          const focalLeft = m.rect.left + (m.rect.width * px) / 100;
          const focalTop = m.rect.top + (m.rect.height * py) / 100;

          // Visible crop = the slice of the source that fills the container.
          const cover = computeCoverRect(m.rect.width, m.rect.height, m.natW, m.natH, px, py);
          const sourceLeft = m.rect.left + cover.left;
          const sourceTop = m.rect.top + cover.top;

          const containerAR = (m.rect.width / m.rect.height).toFixed(2);
          const sourceAR = (m.natW / m.natH).toFixed(2);
          const cropPctY = Math.round(
            ((cover.height - m.rect.height) / cover.height) * 100
          );
          const cropPctX = Math.round(
            ((cover.width - m.rect.width) / cover.width) * 100
          );

          return (
            <div key={i}>
              {/* Container outline */}
              <div
                style={{
                  position: "fixed",
                  left: m.rect.left,
                  top: m.rect.top,
                  width: m.rect.width,
                  height: m.rect.height,
                  border: "1px solid hsl(190 90% 55% / 0.7)",
                  boxSizing: "border-box",
                }}
              />

              {/* Source bounds (the full source projected behind the container) */}
              <div
                style={{
                  position: "fixed",
                  left: sourceLeft,
                  top: sourceTop,
                  width: cover.width,
                  height: cover.height,
                  border: "1px dashed hsl(45 95% 60% / 0.55)",
                  boxSizing: "border-box",
                  // Don't darken — overlays sit on top of real content.
                  background: "transparent",
                }}
              />

              {/* Focal crosshair */}
              <div
                style={{
                  position: "fixed",
                  left: focalLeft - 12,
                  top: focalTop - 12,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  border: "2px solid hsl(0 90% 60%)",
                  boxShadow: "0 0 0 1px white, inset 0 0 0 1px white",
                }}
              />
              <div
                style={{
                  position: "fixed",
                  left: focalLeft - 1,
                  top: focalTop - 18,
                  width: 2,
                  height: 36,
                  background: "hsl(0 90% 60%)",
                }}
              />
              <div
                style={{
                  position: "fixed",
                  left: focalLeft - 18,
                  top: focalTop - 1,
                  width: 36,
                  height: 2,
                  background: "hsl(0 90% 60%)",
                }}
              />

              {/* Label */}
              <div
                style={{
                  position: "fixed",
                  left: Math.max(8, m.rect.left + 8),
                  top: Math.max(8, m.rect.top + 8),
                  font: "11px/1.35 ui-monospace, SFMono-Regular, monospace",
                  color: "white",
                  background: "hsl(0 0% 0% / 0.78)",
                  padding: "6px 8px",
                  borderRadius: 4,
                  whiteSpace: "pre",
                  maxWidth: 320,
                }}
              >
                {`obj-pos: ${posX} ${posY}${m.focalHint ? `  (hint ${m.focalHint})` : ""}
container: ${Math.round(m.rect.width)}×${Math.round(m.rect.height)}  AR ${containerAR}
source:    ${m.natW}×${m.natH}  AR ${sourceAR}
crop:      X ${cropPctX > 0 ? `−${cropPctX}%` : "0%"}  Y ${cropPctY > 0 ? `−${cropPctY}%` : "0%"}`}
              </div>
            </div>
          );
        })}

      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        style={{
          position: "fixed",
          right: 12,
          bottom: 12,
          pointerEvents: "auto",
          font: "11px/1 ui-monospace, monospace",
          color: "white",
          background: "hsl(0 0% 0% / 0.82)",
          border: "1px solid hsl(0 0% 100% / 0.2)",
          padding: "8px 10px",
          borderRadius: 6,
          cursor: "pointer",
        }}
      >
        focus inspector: {collapsed ? "show" : "hide"} ({markers.length})
      </button>
      <button
        type="button"
        onClick={() => {
          try {
            window.localStorage.removeItem("cropFocusInspector");
          } catch {
            // ignore
          }
          setEnabled(false);
        }}
        style={{
          position: "fixed",
          right: 12,
          bottom: 44,
          pointerEvents: "auto",
          font: "11px/1 ui-monospace, monospace",
          color: "white",
          background: "hsl(0 70% 40% / 0.82)",
          border: "1px solid hsl(0 0% 100% / 0.2)",
          padding: "6px 10px",
          borderRadius: 6,
          cursor: "pointer",
        }}
      >
        disable
      </button>
    </div>
  );
};

export default CropFocusInspector;
